import { c as _c } from "react/compiler-runtime";
import { normalizePhoneToE164, PHONE_COUNTRIES, REGION_CODES } from '@onefootprint/core';
import { IcoChevronDown16 } from '@onefootprint/icons';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { Flag, Hint } from '@onefootprint/ui';
import { AsYouType, type CountryCode, getExampleNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import type { ChangeEvent } from 'react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
type PhoneFieldProps = {
  name: string;
  label: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
};
const digitsOf = (value: string) => value.replace(/\D/g, '');

// Countries with a trunk prefix (the UK's 020…) only format nationally once it is typed, and nobody
// types it with the calling code on screen, so fall back to international grouping minus that code.
const formatNational = (digits: string, country: Iso3166TwoDigitCountryCode) => {
  const national = new AsYouType(country as CountryCode).input(digits);
  if (national !== digits || digits.length <= 3) return national;
  const callingCode = REGION_CODES[country];
  const international = new AsYouType().input(`${callingCode}${digits}`);
  return international.startsWith(callingCode) ? international.slice(callingCode.length).trim() : international;
};
const examplePlaceholder = (country: Iso3166TwoDigitCountryCode) => {
  const example = getExampleNumber(country as CountryCode, examples);
  return example ? formatNational(example.nationalNumber, country) : '';
};

// Chrome keeps :focus-visible on a <select> clicked with a mouse, and dismissing its popup fires no
// event, so a CSS-only highlight sticks. A pointer press before focus means the mouse did it.
const useKeyboardFocus = () => {
  const $ = _c(3);
  const [focusVisible, setFocusVisible] = useState(false);
  const pointer = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      onPointerDown: () => {
        pointer.current = true;
        setFocusVisible(false);
      },
      onFocus: () => {
        setFocusVisible(!pointer.current);
        pointer.current = false;
      },
      onBlur: () => {
        setFocusVisible(false);
        pointer.current = false;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== focusVisible) {
    t1 = {
      focusVisible,
      handlers: t0
    };
    $[1] = focusVisible;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const PhoneField = t0 => {
  const $ = _c(54);
  const {
    name,
    label,
    onChange,
    onBlur,
    error
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "demo.form.phoneNumber"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("authentication", t1);
  const id = useId();
  const [country, setCountry] = useState("US");
  const [national, setNational] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const picker = useKeyboardFocus();
  let t2;
  if ($[1] !== onChange) {
    t2 = (digits, nextCountry) => {
      setNational(formatNational(digits, nextCountry));
      onChange(digits ? normalizePhoneToE164(digits, nextCountry as CountryCode) : "");
    };
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const emit = t2;
  let t3;
  if ($[3] !== emit || $[4] !== national) {
    t3 = event => {
      const next = event.target.value as Iso3166TwoDigitCountryCode;
      setCountry(next);
      emit(digitsOf(national), next);
      inputRef.current?.focus();
    };
    $[3] = emit;
    $[4] = national;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleCountry = t3;
  let t4;
  if ($[6] !== id || $[7] !== label) {
    t4 = <label htmlFor={id} className="fp-label fp-custom-appearance flex items-center gap-1 text-label-2 text-primary">{label}</label>;
    $[6] = id;
    $[7] = label;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = Boolean(error);
  const t6 = picker.focusVisible;
  let t7;
  if ($[9] !== country) {
    t7 = <span className="flex scale-80"><Flag code={country} /></span>;
    $[9] = country;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoChevronDown16 color="secondary" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t(_temp);
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = picker.handlers;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = PHONE_COUNTRIES.map(_temp2);
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== country || $[16] !== handleCountry || $[17] !== picker.handlers || $[18] !== t9) {
    t12 = <select aria-label={t9} className="absolute inset-0 cursor-pointer opacity-0" value={country} onChange={handleCountry} {...t10}>{t11}</select>;
    $[15] = country;
    $[16] = handleCountry;
    $[17] = picker.handlers;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== picker.focusVisible || $[21] !== t12 || $[22] !== t7) {
    t13 = <div className="relative flex h-full items-center gap-1 rounded-l-[9px] pl-3 pr-2 transition-colors hover:bg-(--color-gray-800) data-[keyboard-focus=true]:bg-(--color-gray-800)" data-keyboard-focus={t6}>{t7}{t8}{t12}</div>;
    $[20] = picker.focusVisible;
    $[21] = t12;
    $[22] = t7;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = REGION_CODES[country];
  let t15;
  if ($[24] !== t14) {
    t15 = <span className="ml-3 text-body-2 text-secondary">{t14}</span>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = Boolean(error);
  let t17;
  if ($[26] !== country) {
    t17 = examplePlaceholder(country);
    $[26] = country;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== country || $[29] !== emit) {
    t18 = event_0 => {
      const digits_0 = digitsOf(event_0.target.value);
      if (validatePhoneNumberLength(digits_0, country as CountryCode) === "TOO_LONG") {
        return;
      }
      emit(digits_0, country);
    };
    $[28] = country;
    $[29] = emit;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setFocused(true);
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== onBlur) {
    t20 = () => {
      setFocused(false);
      onBlur();
    };
    $[32] = onBlur;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== id || $[35] !== name || $[36] !== national || $[37] !== t16 || $[38] !== t17 || $[39] !== t18 || $[40] !== t20) {
    t21 = <input ref={inputRef} id={id} name={name} type="tel" inputMode="tel" autoComplete="tel-national" className="fp-input fp-phone-input fp-phone-input-field fp-custom-appearance" data-has-error={t16} placeholder={t17} value={national} onChange={t18} onFocus={t19} onBlur={t20} />;
    $[34] = id;
    $[35] = name;
    $[36] = national;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== focused || $[43] !== t13 || $[44] !== t15 || $[45] !== t21 || $[46] !== t5) {
    t22 = <div className="fp-input fp-phone-input fp-phone-input-container fp-custom-appearance items-center" data-size="default" data-disabled="false" data-has-error={t5} data-has-focus={focused}>{t13}{t15}{t21}</div>;
    $[42] = focused;
    $[43] = t13;
    $[44] = t15;
    $[45] = t21;
    $[46] = t5;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== error) {
    t23 = error ? <Hint hasError={true}>{error}</Hint> : null;
    $[48] = error;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23 || $[52] !== t4) {
    t24 = <div className="flex flex-col gap-2">{t4}{t22}{t23}</div>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t4;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  return t24;
};
export default PhoneField;
function _temp($) {
  return $.country;
}
function _temp2(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}